// MiniMap - Square mini map showing 5 zones, NPCs, buildings, and player position
import { memo } from 'react';
import { Lock } from 'lucide-react';
import { ZONES, ISLAND_SIZE, getZone, isZoneUnlocked } from '../../data/zoneData';
import { NPC_DATA } from '../../data/npcData';

const MiniMap = memo(({ playerPos, playerStage, worldObjects, setPlayerPos, setMessage }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const mapSize = isMobile ? 160 : 200;
  const s = mapSize / 200; // scale factor
  const edge = Math.round(60 * s);
  const mid = Math.round(80 * s);
  const half = mapSize / 2;

  return (
    <div
      className="absolute bottom-28 sm:bottom-20 left-2 sm:left-4 z-[270] bg-white/95 backdrop-blur-md rounded-lg sm:rounded-xl border-2 sm:border-3 border-mint-green/50 overflow-hidden shadow-lg"
      style={{ width: mapSize, height: mapSize }}
      data-testid="mini-map"
    >
      <div className="relative w-full h-full">
        {/* Center zone */}
        <div className="absolute" style={{ left: edge, top: edge, width: mid, height: mid, backgroundColor: isZoneUnlocked(ZONES.CENTER, playerStage) ? ZONES.CENTER.color : '#888', opacity: isZoneUnlocked(ZONES.CENTER, playerStage) ? 1 : 0.4 }} />
        {/* North zone */}
        <div className="absolute" style={{ left: edge, top: 0, width: mid, height: edge, backgroundColor: isZoneUnlocked(ZONES.NORTH, playerStage) ? ZONES.NORTH.color : '#888', opacity: isZoneUnlocked(ZONES.NORTH, playerStage) ? 1 : 0.4 }}>
          {!isZoneUnlocked(ZONES.NORTH, playerStage) && <div className="absolute inset-0 flex items-center justify-center"><Lock className="w-3 h-3 sm:w-4 sm:h-4 text-white/80" /></div>}
        </div>
        {/* South zone */}
        <div className="absolute" style={{ left: edge, top: edge + mid, width: mid, height: edge, backgroundColor: isZoneUnlocked(ZONES.SOUTH, playerStage) ? ZONES.SOUTH.color : '#888', opacity: isZoneUnlocked(ZONES.SOUTH, playerStage) ? 1 : 0.4 }}>
          {!isZoneUnlocked(ZONES.SOUTH, playerStage) && <div className="absolute inset-0 flex items-center justify-center"><Lock className="w-3 h-3 sm:w-4 sm:h-4 text-white/80" /></div>}
        </div>
        {/* West zone */}
        <div className="absolute" style={{ left: 0, top: edge, width: edge, height: mid, backgroundColor: isZoneUnlocked(ZONES.WEST, playerStage) ? ZONES.WEST.color : '#888', opacity: isZoneUnlocked(ZONES.WEST, playerStage) ? 1 : 0.4 }}>
          {!isZoneUnlocked(ZONES.WEST, playerStage) && <div className="absolute inset-0 flex items-center justify-center"><Lock className="w-3 h-3 sm:w-4 sm:h-4 text-white/80" /></div>}
        </div>
        {/* East zone */}
        <div className="absolute" style={{ left: edge + mid, top: edge, width: edge, height: mid, backgroundColor: isZoneUnlocked(ZONES.EAST, playerStage) ? ZONES.EAST.color : '#888', opacity: isZoneUnlocked(ZONES.EAST, playerStage) ? 1 : 0.4 }}>
          {!isZoneUnlocked(ZONES.EAST, playerStage) && <div className="absolute inset-0 flex items-center justify-center"><Lock className="w-3 h-3 sm:w-4 sm:h-4 text-white/80" /></div>}
        </div>
        {/* Corner areas */}
        <div className="absolute" style={{ left: 0, top: 0, width: edge, height: edge, backgroundColor: 'rgba(156,163,175,0.5)' }} />
        <div className="absolute" style={{ right: 0, top: 0, width: edge, height: edge, backgroundColor: 'rgba(156,163,175,0.5)' }} />
        <div className="absolute" style={{ left: 0, bottom: 0, width: edge, height: edge, backgroundColor: 'rgba(156,163,175,0.5)' }} />
        <div className="absolute" style={{ right: 0, bottom: 0, width: edge, height: edge, backgroundColor: 'rgba(156,163,175,0.5)' }} />
        {/* Zone labels */}
        <div className="absolute top-1 left-1/2 -translate-x-1/2 text-[7px] sm:text-[8px] font-bold text-white/90">N</div>
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[7px] sm:text-[8px] font-bold text-white/90">S</div>
        <div className="absolute left-1 top-1/2 -translate-y-1/2 text-[7px] sm:text-[8px] font-bold text-white/90">W</div>
        <div className="absolute right-1 top-1/2 -translate-y-1/2 text-[7px] sm:text-[8px] font-bold text-white/90">E</div>
        {/* NPCs on map */}
        {worldObjects.filter(obj => obj.type === 'npc').map(obj => {
          const mapX = half + (obj.x / ISLAND_SIZE) * half;
          const mapY = half + (obj.y / ISLAND_SIZE) * half;
          const zone = getZone(obj.x, obj.y);
          const npcData = NPC_DATA[obj.npcId];
          if (!isZoneUnlocked(zone, playerStage)) return null;
          return (
            <div
              key={obj.id}
              className="absolute cursor-pointer"
              style={{ left: mapX - 4, top: mapY - 4, width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FFD93D', border: '1.5px solid white', zIndex: 15 }}
              title={npcData?.name}
              onClick={(e) => {
                e.stopPropagation();
                if (isZoneUnlocked(zone, playerStage)) {
                  setPlayerPos({ x: obj.x + 30, y: obj.y + 50 });
                  setMessage(`Travelled to ${npcData?.name || obj.npcId}!`);
                  setTimeout(() => setMessage(''), 2000);
                }
              }}
            />
          );
        })}
        {/* Buildings on map */}
        {worldObjects.filter(obj => obj.buildingId).map(obj => {
          const mapX = half + (obj.x / ISLAND_SIZE) * half;
          const mapY = half + (obj.y / ISLAND_SIZE) * half;
          return <div key={obj.id} className="absolute" style={{ left: mapX - 3, top: mapY - 3, width: 6, height: 6, backgroundColor: '#FF6B6B', border: '1px solid white', zIndex: 15 }} />;
        })}
        {/* Player position dot */}
        <div
          className="absolute pointer-events-none"
          style={{ left: half + (playerPos.x / ISLAND_SIZE) * half - 6, top: half + (playerPos.y / ISLAND_SIZE) * half - 6, width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4ECDC4', border: '2px solid white', zIndex: 25, boxShadow: '0 0 8px #4ECDC4' }}
          data-testid="map-player-dot"
        />
        {/* Current zone name */}
        <div className="absolute bottom-0 left-0 right-0 text-center text-[7px] sm:text-[8px] font-bold text-text-dark bg-white/80 py-0.5">
          {getZone(playerPos.x, playerPos.y).name}
        </div>
      </div>
    </div>
  );
});

export default MiniMap;
